import React, { useState, useEffect } from 'react';
import axios from 'axios';

function SeatVisualization() {
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSeatData = async () => {
    try {
      setError(null);
      const response = await axios.get('https://common-sense-test.amun.cs.uchicago.edu/getseatsdata');
      const newSeats = response.data.seats || [];

      // Only update if data has changed to avoid unnecessary re-renders
      setSeats(prevSeats => {
        const prevJson = JSON.stringify(prevSeats);
        const newJson = JSON.stringify(newSeats);
        if (prevJson === newJson) {
          return prevSeats;
        }
        return newSeats;
      });

      // Always update last updated time
      setLastUpdated(new Date().toLocaleTimeString());

      // Only set loading to false after initial load
      setLoading(prevLoading => prevLoading ? false : false);
    } catch (err) {
      console.error('Error fetching seat data:', err);
      setError(err.response?.data?.error || 'Failed to fetch seat data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeatData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSeatData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return timestamp;
    }
  };

  const formatTimestampRange = (range) => {
    if (!range || !range.earliest || !range.latest) return 'N/A';
    try {
      const earliest = formatTimestamp(range.earliest);
      const latest = formatTimestamp(range.latest);
      
      // If they're the same, just show one
      if (range.earliest === range.latest) {
        return earliest;
      }
      
      return `${earliest} - ${latest}`;
    } catch (e) {
      return 'N/A';
    }
  };

  const getStatusColor = (isOccupied) => {
    return isOccupied ? '#dc3545' : '#28a745'; // Red for occupied, green for empty
  };

  const getStatusText = (isOccupied) => {
    return isOccupied ? 'Occupied' : 'Available';
  };

  // Seat ID to grid coordinates mapping
  const seatCoordinates = {
    '1': { x: 0, y: 5 },
    '2': { x: 0, y: 4 },
    '3': { x: 0, y: 3 },
    '4': { x: 0, y: 2 },
    '5': { x: 0, y: 1 },
    '6': { x: 1, y: 0 },
    '7': { x: 2, y: 0 },
    '8': { x: 3, y: 0 },
    '9': { x: 4, y: 0 },
    '10': { x: 5, y: 0 },
    '11': { x: 6, y: 1 },
    '12': { x: 6, y: 2 },
    '13': { x: 6, y: 3 },
    '14': { x: 6, y: 4 },
    '15': { x: 6, y: 5 },
    '16': { x: 2, y: 4 },
    '17': { x: 2, y: 3 },
    '18': { x: 2, y: 2 },
    '19': { x: 4, y: 2 },
    '20': { x: 4, y: 3 },
    '21': { x: 4, y: 4 },
  };

  // Create a map of seat_id to seat data for quick lookup
  const seatMap = {};
  seats.forEach(seat => {
    // Handle both numeric and string seat IDs, extract number if needed
    const seatIdStr = seat.seat_id.toString();
    // Extract numeric part (handles "1", "seat_1", "seat1", etc.)
    const numericMatch = seatIdStr.match(/\d+/);
    if (numericMatch) {
      const numericId = numericMatch[0];
      seatMap[numericId] = seat;
      // Also store with original format for fallback
      seatMap[seatIdStr] = seat;
    } else {
      seatMap[seatIdStr] = seat;
    }
  });

  // Grid dimensions (0-6 for x, 0-5 for y)
  const gridWidth = 7;
  const gridHeight = 6;

  // Create grid cells
  const renderGrid = () => {
    const cells = [];
    
    // Create cells row by row (y from 0 to 5, top to bottom)
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        // Find seat at this coordinate
        const seatId = Object.keys(seatCoordinates).find(
          id => seatCoordinates[id].x === x && seatCoordinates[id].y === y
        );
        
        if (seatId && seatMap[seatId]) {
          const seat = seatMap[seatId];
          const isOccupied = seat.is_occupied;
          
          cells.push(
            <div
              key={`${x}-${y}`}
              style={{
                gridColumn: x + 1,
                gridRow: y + 1,
                backgroundColor: isOccupied ? '#dc3545' : '#28a745',
                color: 'white',
                borderRadius: '8px',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                minHeight: '60px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'background-color 0.5s ease-in-out'
              }}
            >
              {seatId}
            </div>
          );
        }
      }
    }
    
    return cells;
  };

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      transition: 'opacity 0.3s ease-in-out',
      opacity: 1
    }}>
      <h1 style={{ marginBottom: '1rem' }}>Seat Occupancy</h1>
      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Data aggregated from the latest 5 detections per seat.
      </p>
      
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {lastUpdated && (
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Last updated: {lastUpdated}
            </p>
          )}
        </div>
        <button
          onClick={fetchSeatData}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          Error: {error}
        </div>
      )}

      {/* Grid Visualization */}
      {!loading && seats.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ marginBottom: '2rem', fontSize: '1.5rem' }}>CSIL 3 Live Occupancy</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridWidth}, 1fr)`,
              gridTemplateRows: `repeat(${gridHeight}, 1fr)`,
              gap: '0.5rem',
              maxWidth: '800px',
              margin: '0 auto'
            }}
          >
            {renderGrid()}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center', fontSize: '0.9rem', color: '#666' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', backgroundColor: '#28a745', borderRadius: '4px' }}></div>
              <span>Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', backgroundColor: '#dc3545', borderRadius: '4px' }}></div>
              <span>Occupied</span>
            </div>
          </div>
        </div>
      )}

      {loading && seats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading seat data...</p>
        </div>
      ) : seats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No seat data available</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #dee2e6'
                }}>Seat ID</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #dee2e6'
                }}>Status</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #dee2e6'
                }}>Confidence (Median)</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #dee2e6'
                }}>Timestamp Range</th>
              </tr>
            </thead>
            <tbody>
              {seats.map((seat, index) => (
                <tr
                  key={seat.seat_id}
                  style={{
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                  }}
                >
                  <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                    {seat.seat_id}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{
                      color: getStatusColor(seat.is_occupied),
                      fontWeight: 'bold'
                    }}>
                      {getStatusText(seat.is_occupied)}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    {(seat.confidence * 100).toFixed(1)}%
                  </td>
                  <td style={{ padding: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
                    {formatTimestampRange(seat.timestamp_range)}
                    {seat.data_points_count && (
                      <span style={{ display: 'block', fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                        ({seat.data_points_count} data points)
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {seats.length > 0 && (
        <div style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
          <p>Total seats: {seats.length}</p>
          <p>
            Occupied: {seats.filter(s => s.is_occupied).length} | 
            Available: {seats.filter(s => !s.is_occupied).length}
          </p>
        </div>
      )}
    </div>
  );
}

export default SeatVisualization;
